import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import './TargetSpecialtiesSelector.css';

export type MultiSelectOption = { value: string; label: string };

interface Props {
    idPrefix: string;
    title: string;
    note?: string | null;
    options: MultiSelectOption[];
    value?: string[];
    onChange: (ids: string[]) => void;
    disabled?: boolean;
    itemNoun?: string; // e.g. "usertype" / "degree"
    searchPlaceholder?: string;
}

const MultiSelectCheckboxDropdown: React.FC<Props> = ({
    idPrefix,
    title,
    note = null,
    options,
    value,
    onChange,
    disabled = false,
    itemNoun = 'item',
    searchPlaceholder,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [targetAll, setTargetAll] = useState(true);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const ids = Array.isArray(value) ? value.filter(Boolean) : [];
        setTargetAll(ids.length === 0);
        setSelectedIds(new Set(ids));
    }, [value]);

    const notifyChange = useCallback(
        (all: boolean, ids: Set<string>) => {
            onChange(all ? [] : Array.from(ids));
        },
        [onChange],
    );

    const filtered = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return options;
        return options.filter(o => o.label.toLowerCase().includes(q));
    }, [options, searchTerm]);

    const allFilteredSelected =
        filtered.length > 0 && filtered.every(o => selectedIds.has(o.value));

    const handleExpand = () => {
        if (disabled) return;
        setExpanded(prev => !prev);
    };

    const handleTargetAllChange = () => {
        const next = !targetAll;
        setTargetAll(next);
        if (next) notifyChange(true, selectedIds);
    };

    const toggleOne = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            if (!targetAll) notifyChange(false, next);
            return next;
        });
    };

    const toggleAllFiltered = () => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filtered.forEach(o => next.delete(o.value));
            } else {
                filtered.forEach(o => next.add(o.value));
            }
            if (!targetAll) notifyChange(false, next);
            return next;
        });
    };

    const plural =
        selectedIds.size === 1 ? itemNoun : `${itemNoun}${itemNoun.endsWith('s') ? 'es' : 's'}`;
    const summary = targetAll
        ? `All ${itemNoun}s`
        : selectedIds.size === 0
          ? `No ${itemNoun}s selected`
          : `${selectedIds.size} ${plural} selected`;

    return (
        <div className="target-specialties-selector">
            <button
                type="button"
                className="target-specialties-header"
                onClick={handleExpand}
                disabled={disabled}
                aria-expanded={expanded}
            >
                <span className="target-specialties-title">{title}</span>
                <span className="target-specialties-summary text-muted">
                    {summary}
                </span>
                <span className="target-specialties-chevron">
                    {expanded ? '▾' : '▸'}
                </span>
            </button>
            {note ? (
                <p className="target-specialties-note mb-0">{note}</p>
            ) : null}

            {expanded && (
                <div className="target-specialties-body border rounded p-3 mt-3 bg-white">
                    <Form.Check
                        type="switch"
                        id={`${idPrefix}-all`}
                        label="All"
                        checked={targetAll}
                        disabled={disabled}
                        onChange={handleTargetAllChange}
                        className="mb-3"
                    />

                    {!targetAll && (
                        <>
                            <Form.Control
                                type="search"
                                placeholder={
                                    searchPlaceholder || `Search ${itemNoun}s`
                                }
                                value={searchTerm}
                                disabled={disabled}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="mb-3"
                                aria-label={`Search ${itemNoun}s`}
                            />

                            <div className="target-specialties-list">
                                {filtered.length > 0 && (
                                    <Form.Check
                                        type="checkbox"
                                        id={`${idPrefix}-select-all`}
                                        label="Select all"
                                        checked={allFilteredSelected}
                                        disabled={disabled}
                                        onChange={toggleAllFiltered}
                                        className="mb-2 fw-semibold"
                                    />
                                )}
                                {filtered.map(opt => (
                                    <Form.Check
                                        key={opt.value}
                                        type="checkbox"
                                        id={`${idPrefix}-${opt.value}`}
                                        label={opt.label}
                                        checked={selectedIds.has(opt.value)}
                                        disabled={disabled}
                                        onChange={() => toggleOne(opt.value)}
                                        className="mb-1"
                                    />
                                ))}
                                {filtered.length === 0 && (
                                    <p className="text-muted mb-0">
                                        No {itemNoun}s found.
                                    </p>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default MultiSelectCheckboxDropdown;
